import { toHTML, getElement, dataSetToObject } from '../util/dom-util';
import { confineTab } from '../util/a11y';
import EventHandler from '../_dependencyvendor/event-handler';
import UI from './ui-base';

// eslint-disable-next-line no-unused-vars
const VERSION = '1.0.1';
const NAME = 'ui.dialog';

const IDENTIFIER = {
  TRIGGER: 'data-dialog-trigger',
  CLOSE: 'data-dialog-close',
};

const dataAttrConfig = {
  bg: true,
  bgclose: true,
  destroy: false,
  animate: true,
  openClass: 'show',
  closeClass: 'hide',
};

const defaultConfig = {
  ...dataAttrConfig,
  bgTemplate: `
    <div style="
      position: fixed; 
      width: 100%; 
      height: 100%;
      left: 0;
      top: 0;
      background-color: rgba(0,0,0,0.6);">
    </div>`,
};

const ZINDEX = {
  CONTENT: 101,
  BACKDROP: 100,
  INCREASE: 1,
};

class Dialog extends UI {
  constructor(element, config = {}) {
    super(element, config);
    this._setupConfog(config);
    this._bg = null;
    this._closeButtons = null;
    this._isOpen = false;
    this._confineTab = null;
    this._init();
  }

  static get EVENT() {
    return {
      OPEN: `${NAME}.open`,
      OPENED: `${NAME}.opened`,
      CLOSE: `${NAME}.close`,
      CLOSED: `${NAME}.closed`,
      FIRST_OPEN: `${NAME}.firstOpen`,
      LAST_CLOSE: `${NAME}.laseClose`,
    };
  }

  /**
   * 글로벌 설정은 처음에..
   */
  static GLOBAL_CONFIG = {};
  static COUNT = 0;
  static SCROLL_POY = 0;
  /**
   * 현재 활성화 되어있는 dialog 모두 닫기
   */
  static closeAll() {
    const instances = Dialog.getInstances();
    for (const p in instances) {
      if (Object.prototype.hasOwnProperty.call(instances, p)) {
        const dialog = instances[p];
        if (dialog) dialog.close();
      }
    }
  }

  static get NAME() {
    return NAME;
  }

  open() {
    this._open();
  }

  close(e) {
    this._close(e);
  }

  getElement() {
    return this._element;
  }

  _setupConfog(config) {
    this._config = {
      ...defaultConfig,
      ...Dialog.GLOBAL_CONFIG,
      ...config,
      ...dataSetToObject(this._element, dataAttrConfig, 'dialog'),
    };
  }

  _init() {
    this._element.setAttribute('aria-modal', 'true');
  }

  _initVars() {
    this._confineTab = confineTab(this._element);
    this._bg = this._config.bg ? this._createBackground() : null;
    this._closeButtons = this._element.querySelectorAll(`[${IDENTIFIER.CLOSE}]`);
  }

  _initEvents() {
    this._closeButtons.forEach(el => {
      EventHandler.one(el, super._eventName('click'), this._close.bind(this));
    });
  }

  _removeEvents() {
    if (this._closeButtons) {
      this._closeButtons.forEach(el => {
        EventHandler.off(el, super._eventName('click'));
      });
    }
  }

  _removeVars(redraw = false) {
    if (redraw === false) {
      this._bg = null;
    }
    this._closeButtons = null;
  }

  _createBackground() {
    if (this._bg) return this._bg;
    return toHTML(this._config.bgTemplate);
  }

  _zIndexOrderIncrease() {
    this._element.style.zIndex = ZINDEX.CONTENT += ZINDEX.INCREASE;
    if (this._bg) this._bg.style.zIndex = ZINDEX.BACKDROP += ZINDEX.INCREASE;
  }

  _open() {
    if (this._isOpen === true) return;
    this._initVars();
    this._initEvents();
    this._zIndexOrderIncrease();
    this._showBackground();
    this._showDialog();
    this._isOpen = true;
    EventHandler.trigger(this._element, Dialog.EVENT.OPEN, {
      component: this,
    });
    if (Dialog.COUNT === 0) {
      EventHandler.trigger(window, Dialog.EVENT.FIRST_OPEN);
    }
    Dialog.COUNT++;
  }

  _close(e) {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    this._hideDialog();
    EventHandler.trigger(this._element, Dialog.EVENT.CLOSE, {
      component: this,
    });
    if (this._config.animate !== true) {
      this._hideBackground();
      this._confineTab.destroy();
      this.destroy();
    }
  }

  /**
   * 다이얼로그를 닫거나 삭제처리 하지 않고
   * 다이얼로그의 컨텐츠만 새로 그려서 사용할 경우
   * 기존의 이벤트나 기타 처리를 모두 초기화 하고
   * 새로 그려진 엘리먼트에 새로 적용한다.
   */
  redraw() {
    // 기존의 탭 시퀀스 처리를 삭제
    this._confineTab.destroy();
    // 기존의 엘리먼틔 이벤트를 삭제
    this._removeEvents();
    // 기존의 엘리먼트를 근거로 저장했던 변수를 삭제
    this._removeVars(true);

    // 새로 그려진 엘리먼트를 기준으로 변수 초기화
    this._initVars();
    // 새로 그려진 엘리먼트에 이벤트 바인딩( 닫기 버튼 등.. )
    this._initEvents();

    // 새로 그려진 엘리먼트의 탭 시퀀스 처리를 초기화
    const tabInfo = this._confineTab.init();
    // 포커싱 처리.
    if (tabInfo && tabInfo.length > 0) tabInfo[0].focus();
  }

  destroy() {
    this._removeEvents();
    this._removeVars();
    this._isOpen = false;
    if (this._config.destroy === true || this._config.destroy === 'true') {
      this._element.parentNode.removeChild(this._element);
      super.destroy();
    }

    Dialog.COUNT--;
    if (Dialog.COUNT <= 0) {
      EventHandler.trigger(window, Dialog.EVENT.LAST_CLOSE);
    }
  }

  _showBackground() {
    if (this._bg) {
      document.body.appendChild(this._bg);
      // eslint-disable-next-line no-unused-vars
      const reflow = this._bg.offsetHeight;
      this._bg.classList.add('fadeIn');
    }
  }

  _showDialog() {
    // eslint-disable-next-line no-unused-vars
    const reflow = this._element.offsetHeight;
    if (this._config.animate === true) {
      EventHandler.one(this._element, 'animationend', () => {
        const focusable = this._confineTab.init();
        focusable[0].focus();
        EventHandler.trigger(this._element, Dialog.EVENT.OPENED, {
          component: this,
        });
      });
    } else {
      this._confineTab.init();
    }
    this._element.classList.add(this._config.openClass);
  }

  _hideBackground() {
    if (this._bg) document.body.removeChild(this._bg);
  }

  _hideDialog() {
    this._element.classList.add(this._config.closeClass);
    this._element.classList.remove(this._config.openClass);
    EventHandler.one(this._element, 'animationend', () => {
      this._element.classList.remove(this._config.closeClass);
      this._hideBackground();
      EventHandler.trigger(this._element, Dialog.EVENT.CLOSED, {
        component: this,
      });
      this._confineTab.destroy();
      this.destroy();
    });
  }
}

EventHandler.on(document, `click.DIALOG_TRIGGER`, event => {
  const el = event.target.closest(`[${IDENTIFIER.TRIGGER}]`);
  if (!el) return;
  const target = el.getAttribute(IDENTIFIER.TRIGGER);
  if (target) {
    const dialogElement = getElement(target);
    if (dialogElement) {
      const dialog = Dialog.getInstance(dialogElement);
      if (dialog) {
        dialog.open();
      } else {
        new Dialog(dialogElement).open();
      }
    }
  }
});

export { defaultConfig };
export default Dialog;
